import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Chip,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  Paper,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  AccountTree as TreeIcon,
  Delete as DeleteIcon,
  ExpandLess,
  ExpandMore,
  FormatListBulleted as FlatIcon,
  Search as SearchIcon,
} from '@mui/icons-material';

import {
  flattenLevels,
  type FlatLevelEntry,
} from '../../lib/schemaTopology';
import type { OutputStructure } from '../../lib/outputStructure';

export type NavSelection =
  | { kind: 'level'; pathKey: string }
  | { kind: 'table'; tableName: string }
  | null;

export interface HierarchyTreeNavProps {
  /** Optional output_structure — when present, tree mode is available. */
  outputStructure?: OutputStructure | null;
  /** All table names (flat). */
  tableNames: string[];
  /** Tables not referenced by any level. Rendered under "Other tables". */
  orphanTables: string[];
  /** Map of level pathKey → table name. */
  levelToTable: Record<string, string>;
  /** Current selection. */
  selected: NavSelection;
  onSelect: (sel: NavSelection) => void;
  /** Edit-mode actions on the tree (rename level, add child, remove). */
  mode?: 'view' | 'edit';
  onLevelRename?: (pathKey: string, nextName: string) => void;
  onAddChildLevel?: (parentPathKey: string | null) => void;
  onRemoveLevel?: (pathKey: string, siblingIndex: number) => void;
  /** Dirty markers — tree nodes / tables with unsaved edits. */
  dirtyLevels?: Set<string>;
  dirtyTables?: Set<string>;
  /** Override the default 320px panel width (controlled externally for resize). */
  width?: number;
}

/**
 * Left pane of the unified schema browser. Tree (from output_structure)
 * or flat list (from tables). Search filters both. The toggle is hidden
 * when only one mode is meaningful (no hierarchy → flat only, single
 * table → no toggle).
 */
const HierarchyTreeNav: React.FC<HierarchyTreeNavProps> = ({
  outputStructure,
  tableNames,
  orphanTables,
  levelToTable,
  selected,
  onSelect,
  mode = 'view',
  onLevelRename,
  onAddChildLevel,
  onRemoveLevel,
  dirtyLevels,
  dirtyTables,
  width,
}) => {
  const hasHierarchy = !!outputStructure && (outputStructure.levels || []).length > 0;
  const [view, setView] = useState<'tree' | 'flat'>(hasHierarchy ? 'tree' : 'flat');
  const [query, setQuery] = useState('');
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!hasHierarchy && view === 'tree') setView('flat');
  }, [hasHierarchy, view]);

  const flatLevels: FlatLevelEntry[] = useMemo(
    () => (hasHierarchy ? flattenLevels(outputStructure) : []),
    [hasHierarchy, outputStructure],
  );

  const visibleLevels = useMemo(() => {
    const q = query.trim().toLowerCase();
    return flatLevels.filter((entry) => {
      // Apply collapse filter: a node is hidden when any ancestor is collapsed.
      for (let i = 1; i < entry.path.length; i++) {
        const ancestor = entry.path.slice(0, i).join('/');
        if (collapsed.has(ancestor)) return false;
      }
      if (!q) return true;
      if (entry.level.element_name.toLowerCase().includes(q)) return true;
      const tableName = levelToTable[entry.pathKey] || '';
      if (tableName.toLowerCase().includes(q)) return true;
      return false;
    });
  }, [flatLevels, collapsed, query, levelToTable]);

  const sortedTables = useMemo(() => [...tableNames].sort(), [tableNames]);
  const visibleTables = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return sortedTables;
    return sortedTables.filter((t) => t.toLowerCase().includes(q));
  }, [sortedTables, query]);
  const visibleOrphans = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return orphanTables;
    return orphanTables.filter((t) => t.toLowerCase().includes(q));
  }, [orphanTables, query]);

  const toggleCollapse = (pathKey: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(pathKey)) next.delete(pathKey);
      else next.add(pathKey);
      return next;
    });
  };

  return (
    <Paper
      variant="outlined"
      sx={{
        width: { xs: '100%', md: width ?? 320 },
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
      }}
    >
      {/* Header: toggle + search */}
      <Box sx={{ p: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
        {hasHierarchy && (
          <ToggleButtonGroup
            size="small"
            exclusive
            value={view}
            onChange={(_, v) => v && setView(v)}
            fullWidth
            sx={{ mb: 1 }}
          >
            <ToggleButton value="tree">
              <TreeIcon fontSize="small" sx={{ mr: 0.5 }} />
              Tree
            </ToggleButton>
            <ToggleButton value="flat">
              <FlatIcon fontSize="small" sx={{ mr: 0.5 }} />
              Flat
            </ToggleButton>
          </ToggleButtonGroup>
        )}
        <TextField
          placeholder="Search levels or tables"
          size="small"
          fullWidth
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* Body */}
      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
        {view === 'tree' && hasHierarchy ? (
          <List dense disablePadding>
            {visibleLevels.map((entry) => {
              // Calculate the correct sibling index within parent's children array
              const path = entry.path;
              let siblingIndex = 0;
              
              if (path.length === 1) {
                // Top-level element - find index in root levels array
                const rootLevels = outputStructure?.levels || [];
                siblingIndex = rootLevels.findIndex(lvl => lvl.element_name === path[0]);
                if (siblingIndex === -1) siblingIndex = 0; // Fallback to 0 if not found
              } else {
                // Nested element - find index within parent's children array
                const parentPath = path.slice(0, -1);
                const elementName = path[path.length - 1];
                
                // Find the parent level
                let parentLevel: any = null;
                const walkToParent = (levels: any[], currentPath: string[]): any => {
                  if (currentPath.length === 0) return null;
                  
                  const targetName = currentPath[0];
                  const level = levels.find(l => l.element_name === targetName);
                  
                  if (!level) return null;
                  if (currentPath.length === 1) return level;
                  
                  return walkToParent(level.children || [], currentPath.slice(1));
                };
                
                parentLevel = walkToParent(outputStructure?.levels || [], parentPath);
                if (parentLevel && parentLevel.children) {
                  siblingIndex = parentLevel.children.findIndex((child: any) => child.element_name === elementName);
                  if (siblingIndex === -1) siblingIndex = 0; // Fallback to 0 if not found
                }
              }
              return (
              <TreeRow
                key={`${entry.pathKey}#${siblingIndex}`}
                entry={entry}
                isCollapsed={collapsed.has(entry.pathKey)}
                hasChildren={(entry.level.children || []).length > 0}
                isSelected={
                  selected?.kind === 'level' && selected.pathKey === entry.pathKey
                }
                isDirty={dirtyLevels?.has(entry.pathKey)}
                onSelect={() => onSelect({ kind: 'level', pathKey: entry.pathKey })}
                onToggleCollapse={() => toggleCollapse(entry.pathKey)}
                tableName={levelToTable[entry.pathKey]}
                mode={mode}
                onRename={(name) => onLevelRename?.(entry.pathKey, name)}
                onAddChild={() => onAddChildLevel?.(entry.pathKey)}
                onRemove={() => onRemoveLevel?.(entry.pathKey, siblingIndex)}
              />
            );
            })}
            {visibleOrphans.length > 0 && (
              <>
                <Box
                  sx={{
                    px: 2,
                    py: 0.75,
                    bgcolor: 'grey.100',
                    borderTop: '1px solid',
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    mt: 1,
                  }}
                >
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>
                    Other tables
                  </Typography>
                </Box>
                {visibleOrphans.map((t) => (
                  <FlatRow
                    key={t}
                    tableName={t}
                    isSelected={
                      selected?.kind === 'table' && selected.tableName === t
                    }
                    isDirty={dirtyTables?.has(t)}
                    onSelect={() => onSelect({ kind: 'table', tableName: t })}
                  />
                ))}
              </>
            )}
            {visibleLevels.length === 0 && visibleOrphans.length === 0 && (
              <Box sx={{ p: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  No matches.
                </Typography>
              </Box>
            )}
          </List>
        ) : (
          <List dense disablePadding>
            {visibleTables.map((t) => (
              <FlatRow
                key={t}
                tableName={t}
                isSelected={selected?.kind === 'table' && selected.tableName === t}
                isDirty={dirtyTables?.has(t)}
                onSelect={() => onSelect({ kind: 'table', tableName: t })}
              />
            ))}
            {visibleTables.length === 0 && (
              <Box sx={{ p: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  {query ? `No tables match "${query}".` : 'No tables.'}
                </Typography>
              </Box>
            )}
          </List>
        )}
      </Box>
    </Paper>
  );
};

interface TreeRowProps {
  entry: FlatLevelEntry;
  isCollapsed: boolean;
  hasChildren: boolean;
  isSelected: boolean;
  isDirty?: boolean;
  onSelect: () => void;
  onToggleCollapse: () => void;
  tableName?: string;
  mode: 'view' | 'edit';
  onRename: (name: string) => void;
  onAddChild: () => void;
  onRemove: () => void;
}

const TreeRow: React.FC<TreeRowProps> = ({
  entry,
  isCollapsed,
  hasChildren,
  isSelected,
  isDirty,
  onSelect,
  onToggleCollapse,
  tableName,
  mode,
  onRename,
  onAddChild,
  onRemove,
}) => {
  const indent = entry.depth * 14;
  const [editingName, setEditingName] = useState(false);
  const [draftName, setDraftName] = useState(entry.level.element_name);

  useEffect(() => {
    setDraftName(entry.level.element_name);
  }, [entry.level.element_name]);

  return (
    <ListItemButton
      selected={isSelected}
      onClick={onSelect}
      sx={{
        py: 0.5,
        pl: `${8 + indent}px`,
        borderLeft: '3px solid',
        borderLeftColor: isSelected ? 'primary.main' : 'transparent',
      }}
    >
      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ width: '100%', minWidth: 0 }}>
        {hasChildren ? (
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onToggleCollapse();
            }}
            sx={{ p: 0 }}
          >
            {isCollapsed ? <ExpandMore fontSize="small" /> : <ExpandLess fontSize="small" />}
          </IconButton>
        ) : (
          <Box sx={{ width: 22 }} />
        )}
        {mode === 'edit' && editingName ? (
          <TextField
            value={draftName}
            size="small"
            variant="standard"
            autoFocus
            onChange={(e) => setDraftName(e.target.value)}
            onBlur={() => {
              setEditingName(false);
              if (draftName !== entry.level.element_name) {
                onRename(draftName);
              }
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
              if (e.key === 'Escape') {
                setDraftName(entry.level.element_name);
                setEditingName(false);
              }
            }}
            onClick={(e) => e.stopPropagation()}
            sx={{ flex: 1 }}
          />
        ) : (
          <Typography
            variant="body2"
            fontWeight={500}
            noWrap
            sx={{ flex: 1 }}
            onDoubleClick={
              mode === 'edit'
                ? (e) => {
                    e.stopPropagation();
                    setEditingName(true);
                  }
                : undefined
            }
          >
            {entry.level.element_name}
          </Typography>
        )}
        {isDirty && (
          <Box
            sx={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              bgcolor: 'warning.main',
              flexShrink: 0,
            }}
          />
        )}
        {tableName && (
          <Tooltip title={`Table: ${tableName}`}>
            <Chip
              label={tableName}
              size="small"
              variant="outlined"
              sx={{ height: 18, fontSize: 10, '& .MuiChip-label': { px: 0.75 } }}
            />
          </Tooltip>
        )}
        {mode === 'edit' && (
          <Tooltip title="Remove element">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onRemove();
              }}
              sx={{ p: 0.25 }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Stack>
    </ListItemButton>
  );
};

interface FlatRowProps {
  tableName: string;
  isSelected: boolean;
  isDirty?: boolean;
  onSelect: () => void;
}

const FlatRow: React.FC<FlatRowProps> = ({ tableName, isSelected, isDirty, onSelect }) => (
  <ListItemButton
    selected={isSelected}
    onClick={onSelect}
    sx={{
      py: 0.75,
      pl: 2,
      borderLeft: '3px solid',
      borderLeftColor: isSelected ? 'primary.main' : 'transparent',
    }}
  >
    <Stack direction="row" alignItems="center" spacing={0.5} sx={{ width: '100%' }}>
      <Typography variant="body2" fontWeight={500} sx={{ flex: 1 }} noWrap>
        {tableName}
      </Typography>
      {isDirty && (
        <Box
          sx={{
            width: 6,
            height: 6,
            borderRadius: '50%',
            bgcolor: 'warning.main',
            flexShrink: 0,
          }}
        />
      )}
    </Stack>
  </ListItemButton>
);

export default HierarchyTreeNav;
